// frontend/src/components/planner/BusinessEntitiesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Plus,
  Edit2,
  Trash2,
  Building2,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  X,
  Link2,
  Unlink,
  CreditCard,
  Landmark
} from 'lucide-react';
import api, { TokenStorage } from '../../utils/api';
import AppShell from '../layout/AppShell';

const ENTITY_TYPES = [
  { value: 's_corp', label: 'S-Corporation', description: 'Pass-through taxation with liability protection' },
  { value: 'c_corp', label: 'C-Corporation', description: 'Double taxation, unlimited growth potential' },
  { value: 'llc', label: 'LLC', description: 'Flexible structure, pass-through or corporate taxation' },
  { value: 'sole_prop', label: 'Sole Proprietorship', description: 'Simplest structure, personal liability' },
  { value: 'partnership', label: 'Partnership', description: 'Multiple owners sharing profits and losses' }
];

const ENTITY_TYPE_LABELS = {
  s_corp: 'S-Corp',
  c_corp: 'C-Corp',
  llc: 'LLC',
  sole_prop: 'Sole Prop',
  partnership: 'Partnership'
};

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const ACCOUNT_ROLES = [
  { value: 'operating', label: 'Operating', description: 'Primary business checking' },
  { value: 'payroll', label: 'Payroll', description: 'Payroll disbursements' },
  { value: 'savings', label: 'Savings', description: 'Business savings / reserve' },
  { value: 'credit', label: 'Credit', description: 'Business credit card' },
  { value: 'tax', label: 'Tax', description: 'Tax withholding account' }
];

const ACCOUNT_ROLE_LABELS = {
  operating: 'Operating',
  payroll: 'Payroll',
  savings: 'Savings',
  credit: 'Credit',
  tax: 'Tax'
};

// Helper: account display name
function accountDisplayName(acct) {
  const name = acct.nickname || acct.account_name || acct.name || 'Account';
  const mask = acct.mask ? ` ••${acct.mask}` : '';
  return `${name}${mask}`;
}

// Helper: account type icon
function AccountIcon({ type, className = "w-4 h-4" }) {
  if (type === 'credit') return <CreditCard className={className} />;
  return <Landmark className={className} />;
}

// ============================================================================
// Account Link Modal — pick which accounts to link to this entity
// ============================================================================
function AccountLinkModal({ entity, onClose, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // bank_account_id being linked
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('operating');

  useEffect(() => {
    loadAvailableAccounts();
  }, []);

  async function loadAvailableAccounts() {
    try {
      setLoading(true);
      const res = await api.get(`/api/business-entities/${entity.id}/accounts/available`);
      setAccounts(res.data.accounts || []);
    } catch (err) {
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }

  async function handleLink(bankAccountId) {
    setSaving(bankAccountId);
    setError('');
    try {
      await api.post(`/api/business-entities/${entity.id}/accounts`, {
        bank_account_id: bankAccountId,
        account_role: selectedRole
      });
      await loadAvailableAccounts();
      // Don't close — let user link multiple
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link account');
    } finally {
      setSaving(null);
    }
  }

  async function handleUnlink(acct) {
    setSaving(acct.bank_account_id);
    setError('');
    try {
      await api.delete(`/api/business-entities/${acct.linked_entity_id}/accounts/${acct.link_id}`);
      await loadAvailableAccounts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unlink account');
    } finally {
      setSaving(null);
    }
  }

  // Split accounts into groups
  const linkedToThis = accounts.filter(a => a.linked_entity_id === entity.id);
  const linkedElsewhere = accounts.filter(a => a.linked_entity_id && a.linked_entity_id !== entity.id);
  const unlinked = accounts.filter(a => !a.linked_entity_id);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-emerald-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Link Accounts</h2>
              <p className="text-xs text-gray-500">{entity.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Role selector */}
        <div className="px-5 pt-4 pb-2 border-b flex-shrink-0">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Default role for new links:</label>
          <div className="flex flex-wrap gap-1.5">
            {ACCOUNT_ROLES.map(role => (
              <button
                key={role.value}
                type="button"
                onClick={() => setSelectedRole(role.value)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  selectedRole === role.value
                    ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {role.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mx-5 mt-3 flex items-center gap-2 p-2.5 bg-red-50 text-red-700 rounded-lg text-xs">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Account list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
            </div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No bank accounts found. Link a bank via Plaid first.
            </p>
          ) : (
            <>
              {/* Already linked to this entity */}
              {linkedToThis.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Linked to {entity.name} ({linkedToThis.length})
                  </h3>
                  <div className="space-y-1.5">
                    {linkedToThis.map(acct => (
                      <div key={acct.bank_account_id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <AccountIcon type={acct.type} className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{accountDisplayName(acct)}</div>
                            <div className="text-xs text-gray-500">{acct.institution_name || 'Unknown'} · {ACCOUNT_ROLE_LABELS[acct.account_role] || acct.account_role}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnlink(acct)}
                          disabled={saving === acct.bank_account_id}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
                          title="Unlink"
                        >
                          {saving === acct.bank_account_id ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                          ) : (
                            <Unlink className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available (unlinked) accounts */}
              {unlinked.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Personal / Unlinked ({unlinked.length})
                  </h3>
                  <div className="space-y-1.5">
                    {unlinked.map(acct => (
                      <div key={acct.bank_account_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-emerald-200 transition-colors">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <AccountIcon type={acct.type} className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{accountDisplayName(acct)}</div>
                            <div className="text-xs text-gray-500">{acct.institution_name || 'Unknown'} · {acct.subtype || acct.type}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleLink(acct.bank_account_id)}
                          disabled={saving === acct.bank_account_id}
                          className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors flex-shrink-0"
                        >
                          {saving === acct.bank_account_id ? (
                            <div className="w-3.5 h-3.5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" />
                          ) : (
                            'Link'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Linked to other entities */}
              {linkedElsewhere.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Linked to Other Entities ({linkedElsewhere.length})
                  </h3>
                  <div className="space-y-1.5">
                    {linkedElsewhere.map(acct => (
                      <div key={acct.bank_account_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 opacity-60">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <AccountIcon type={acct.type} className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-700 truncate">{accountDisplayName(acct)}</div>
                            <div className="text-xs text-gray-400">{acct.institution_name || 'Unknown'} · linked to {acct.linked_entity_name}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t flex-shrink-0">
          <button
            onClick={() => { onSave(); onClose(); }}
            className="w-full sm:w-auto px-5 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors min-h-[44px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Business Entity Form Modal
// ============================================================================
function BusinessEntityModal({ entity, onClose, onSave }) {
  const isEdit = !!entity;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState(entity?.name || '');
  const [dbaName, setDbaName] = useState(entity?.dba_name || '');
  const [entityType, setEntityType] = useState(entity?.entity_type || 's_corp');
  const [ein, setEin] = useState(entity?.ein || '');
  const [stateId, setStateId] = useState(entity?.state_id || '');
  const [fiscalYearStart, setFiscalYearStart] = useState(entity?.fiscal_year_start || 1);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Business name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        dba_name: dbaName.trim() || null,
        entity_type: entityType,
        ein: ein.trim() || null,
        state_id: stateId.trim() || null,
        fiscal_year_start: fiscalYearStart
      };

      if (isEdit) {
        await api.patch(`/api/business-entities/${entity.id}`, payload);
      } else {
        await api.post('/api/business-entities', payload);
      }

      onSave();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save business entity';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  // Format EIN as user types: XX-XXXXXXX
  function handleEinChange(val) {
    const digits = val.replace(/\D/g, '').slice(0, 9);
    if (digits.length > 2) {
      setEin(`${digits.slice(0, 2)}-${digits.slice(2)}`);
    } else {
      setEin(digits);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-emerald-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit Business Entity' : 'Add Business Entity'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Business Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. May Media LLC"
              className="w-full px-3 py-2.5 sm:py-2 text-base border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              autoFocus
            />
          </div>

          {/* DBA Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DBA Name <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={dbaName}
              onChange={e => setDbaName(e.target.value)}
              placeholder="Doing Business As..."
              className="w-full px-3 py-2.5 sm:py-2 text-base border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            />
          </div>

          {/* Entity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entity Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ENTITY_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setEntityType(type.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all min-h-[44px] ${
                    entityType === type.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900">{type.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* EIN + State ID row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                EIN <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={ein}
                onChange={e => handleEinChange(e.target.value)}
                placeholder="XX-XXXXXXX"
                maxLength={10}
                className="w-full px-3 py-2.5 sm:py-2 text-base border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State Tax ID <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={stateId}
                onChange={e => setStateId(e.target.value)}
                placeholder="State ID"
                className="w-full px-3 py-2.5 sm:py-2 text-base border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
          </div>

          {/* Fiscal Year Start */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fiscal Year Start
            </label>
            <select
              value={fiscalYearStart}
              onChange={e => setFiscalYearStart(parseInt(e.target.value))}
              className="w-full px-3 py-2.5 sm:py-2 text-base border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-5 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>{isEdit ? 'Save Changes' : 'Create Business'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Delete Confirmation Modal
// ============================================================================
function DeleteConfirmModal({ entity, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete(`/api/business-entities/${entity.id}`);
      onConfirm();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-sm p-5 sm:p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Deactivate Business?</h3>
            <p className="text-sm text-gray-500">{entity.name}</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          This will deactivate the business entity. Linked accounts and transactions will be preserved.
          You can reactivate it later if needed.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px]"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px]"
          >
            {deleting ? 'Deactivating...' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Panel
// ============================================================================
export default function BusinessEntitiesPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editEntity, setEditEntity] = useState(null);
  const [deleteEntity, setDeleteEntity] = useState(null);
  const [linkEntity, setLinkEntity] = useState(null);

  // Expanded entity cards (show linked accounts inline)
  const [expandedId, setExpandedId] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState({}); // { entityId: [accounts] }

  useEffect(() => {
    loadEntities();
  }, []);

  async function loadEntities() {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/api/business-entities');
      setEntities(res.data.businessEntities || []);
    } catch (err) {
      if (err.response?.status === 401) {
        TokenStorage.removeToken();
        navigate('/login');
        return;
      }
      setError('Failed to load business entities');
    } finally {
      setLoading(false);
    }
  }

  const loadLinkedAccounts = useCallback(async (entityId) => {
    try {
      const res = await api.get(`/api/business-entities/${entityId}/accounts`);
      setLinkedAccounts(prev => ({ ...prev, [entityId]: res.data.accounts || [] }));
    } catch (err) {
      console.error('Failed to load linked accounts:', err);
    }
  }, []);

  function toggleExpand(entityId) {
    if (expandedId === entityId) {
      setExpandedId(null);
    } else {
      setExpandedId(entityId);
      if (!linkedAccounts[entityId]) {
        loadLinkedAccounts(entityId);
      }
    }
  }

  function handleSaveSuccess() {
    setShowAddModal(false);
    setEditEntity(null);
    setSuccess(editEntity ? 'Business entity updated' : 'Business entity created');
    setTimeout(() => setSuccess(''), 3000);
    loadEntities();
  }

  function handleDeleteSuccess() {
    setDeleteEntity(null);
    setSuccess('Business entity deactivated');
    setTimeout(() => setSuccess(''), 3000);
    loadEntities();
  }

  function handleLinkSuccess() {
    setSuccess('Account links updated');
    setTimeout(() => setSuccess(''), 3000);
    // Reload linked accounts for the entity that was being linked
    if (linkEntity) {
      loadLinkedAccounts(linkEntity.id);
    }
  }

  // Mask EIN for display: XX-***XXXX → show first 2 and last 4
  function maskEin(ein) {
    if (!ein) return null;
    const digits = ein.replace(/\D/g, '');
    if (digits.length < 9) return '**-*******';
    return `${digits.slice(0, 2)}-***${digits.slice(5)}`;
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-emerald-600" />
              Business Entities
            </h1>
            <p className="text-base sm:text-sm text-gray-500 mt-1">
              Manage your business entities for separate forecasting and expense tracking
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-base sm:text-sm font-medium min-h-[44px]"
          >
            <Plus className="w-4 h-4" />
            Add Business
          </button>
        </div>

        {/* Status messages */}
        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-emerald-50 text-emerald-700 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* Entity List */}
        {entities.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-gray-300 p-10 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-700 mb-1">No business entities yet</h3>
            <p className="text-base sm:text-sm text-gray-500 mb-4">
              Add a business entity to start tracking business expenses separately from personal.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] sm:min-h-0 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-base sm:text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Your First Business
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {entities.map(entity => {
              const isExpanded = expandedId === entity.id;
              const accts = linkedAccounts[entity.id] || [];

              return (
                <div
                  key={entity.id}
                  className={`bg-white rounded-lg border transition-colors ${isExpanded ? 'border-emerald-300 shadow-sm' : 'border-gray-200 hover:border-emerald-200'}`}
                >
                  {/* Entity header row */}
                  <div className="flex items-start justify-between p-5">
                    <div 
                      className="flex items-start gap-3 flex-1 cursor-pointer"
                      onClick={() => toggleExpand(entity.id)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{entity.name}</h3>
                        {entity.dba_name && (
                          <p className="text-sm text-gray-500">DBA: {entity.dba_name}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                            {ENTITY_TYPE_LABELS[entity.entity_type] || entity.entity_type}
                          </span>
                          {entity.ein && (
                            <span className="text-xs text-gray-400 font-mono">
                              EIN: {maskEin(entity.ein)}
                            </span>
                          )}
                          {entity.fiscal_year_start !== 1 && (
                            <span className="text-xs text-gray-400">
                              FY starts {MONTHS.find(m => m.value === entity.fiscal_year_start)?.label}
                            </span>
                          )}
                          {accts.length > 0 && (
                            <span className="text-xs text-emerald-600 font-medium">
                              {accts.length} account{accts.length !== 1 ? 's' : ''} linked
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-1 ml-2 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditEntity(entity); }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5 sm:w-4 sm:h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteEntity(entity); }}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                        title="Deactivate"
                      >
                        <Trash2 className="w-5 h-5 sm:w-4 sm:h-4 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded: Linked accounts */}
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                            <Link2 className="w-4 h-4 text-gray-400" />
                            Linked Bank Accounts
                          </h4>
                          <button
                            onClick={() => setLinkEntity(entity)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Link Account
                          </button>
                        </div>

                        {accts.length === 0 ? (
                          <div className="py-4 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
                            <p className="text-sm text-gray-500">No accounts linked yet</p>
                            <button
                              onClick={() => setLinkEntity(entity)}
                              className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              Link your first account →
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {accts.map(acct => (
                              <div
                                key={acct.link_id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <AccountIcon type={acct.type} className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {acct.nickname || acct.account_name || 'Account'}{acct.mask ? ` ••${acct.mask}` : ''}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {acct.institution_name || 'Unknown'} · {ACCOUNT_ROLE_LABELS[acct.account_role] || acct.account_role}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info callout */}
        {entities.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-base sm:text-sm text-blue-700">
              <strong>Tip:</strong> Click an entity to expand it and see linked accounts.
              Unlinked bank accounts are treated as personal by default.
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <BusinessEntityModal
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveSuccess}
        />
      )}
      {editEntity && (
        <BusinessEntityModal
          entity={editEntity}
          onClose={() => setEditEntity(null)}
          onSave={handleSaveSuccess}
        />
      )}
      {deleteEntity && (
        <DeleteConfirmModal
          entity={deleteEntity}
          onClose={() => setDeleteEntity(null)}
          onConfirm={handleDeleteSuccess}
        />
      )}
      {linkEntity && (
        <AccountLinkModal
          entity={linkEntity}
          onClose={() => setLinkEntity(null)}
          onSave={handleLinkSuccess}
        />
      )}
    </AppShell>
  );
}
